import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { newsData } from '../data/newsData';

export const SITE_URL = 'https://www.genionaitech.com';
const SITE_NAME = 'Genion Quantum Technologies';

const DEFAULT_META = {
  title: 'Genion Quantum Technologies | AI & Quantum-Powered Drug Discovery',
  description:
    'Genion Quantum Technologies combines AI, large language models, and quantum mechanics to accelerate drug discovery — de novo small-molecule and peptide design, BBB prediction, and in silico ADMET profiling.',
};

// Per-route title and description. Keep descriptions under ~160 characters.
const ROUTE_META = {
  '/': DEFAULT_META,
  '/services': {
    title: 'AI Drug Discovery Services | Genion Quantum Technologies',
    description:
      'AI-based small-molecule design, molecular optimization, ADMET property prediction, and quantum calculations with spectroscopic analysis (IR, NMR, UV-Vis, Raman).',
  },
  '/platform-overview': {
    title: 'AI Drug Discovery Platform Overview | Genion Quantum Technologies',
    description:
      'An overview of Genion’s AI and quantum-mechanics drug discovery platform for small molecules, peptides, and blood-brain barrier (BBB) permeability prediction.',
  },
  '/technologies': {
    title: 'AI + Quantum Simulation Technologies | Genion Quantum Technologies',
    description:
      'AI-driven molecular generation and screening, AI + quantum simulation optimization, closed-loop AI–experiment optimization, and LLM-enhanced molecular reasoning.',
  },
  '/intellectual-property': {
    title: 'Intellectual Property & Technology Portfolio | Genion Quantum Technologies',
    description:
      'Genion’s proprietary technology portfolio in AI-driven drug discovery, molecular design, and quantum chemistry, with commercial applications across pharma R&D.',
  },
  '/small-molecule-drugs': {
    title: 'AI Small-Molecule Drug Design | Genion Quantum Technologies',
    description:
      'De novo small-molecule lead generation with LLM-Transformer and graph neural network (GNN) models, backed by a bioactivity database covering 50,000+ targets.',
  },
  '/peptide-based-drugs': {
    title: 'AI Peptide Drug Discovery | Genion Quantum Technologies',
    description:
      'Sequence-based AI peptide generation integrated with structure-based modeling and protein–protein interaction (PPI) analysis to optimize peptide therapeutics.',
  },
  '/bbb-prediction': {
    title: 'Blood-Brain Barrier (BBB) Permeability Prediction | Genion Quantum Technologies',
    description:
      'Predict CNS drug brain penetration early: BBB, P-gp, PAMPA, Caco-2, PSA, and logP endpoints combined into one interpretable multi-parameter BBB score.',
  },
  '/publication': {
    title: 'Publications | Genion Quantum Technologies',
    description:
      'Research papers and preprints from Genion Quantum Technologies, including SpCLM, a zero-shot chemical language model for molecular optimization.',
  },
  '/news': {
    title: 'News & Press Releases | Genion Quantum Technologies',
    description:
      'Latest news, press releases, and research announcements from Genion Quantum Technologies on AI-driven drug discovery.',
  },
  '/partnership': {
    title: 'Partnerships | Genion Quantum Technologies',
    description:
      'Partner with Genion Quantum Technologies to accelerate drug discovery with AI and quantum mechanics. Explore our research and industry collaborations.',
  },
  '/membership': {
    title: 'Membership Plans | Genion Quantum Technologies',
    description:
      'Membership plans with access to Genion’s AI-driven drug discovery platform and quantum mechanics integration. Choose the plan that fits your research needs.',
  },
  '/request-quote': {
    title: 'Request an Enterprise Quote | Genion Quantum Technologies',
    description:
      'Request an enterprise quote for AI drug discovery, molecular design, BBB prediction, or quantum chemistry services from Genion Quantum Technologies.',
  },
  '/company-overview': {
    title: 'About Us | Genion Quantum Technologies',
    description:
      'Genion Quantum Technologies, based in Rockville, MD, pioneers the integration of AI and quantum mechanics to make drug discovery faster and more efficient.',
  },
  '/contact': {
    title: 'Contact Us | Genion Quantum Technologies',
    description:
      'Get in touch with Genion Quantum Technologies to learn more about our AI-driven drug discovery platform and services.',
  },
};

const truncate = (text, max = 160) =>
  text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;

const getMeta = (pathname) => {
  const newsMatch = pathname.match(/^\/news\/([^/]+)$/);
  if (newsMatch) {
    const article = newsData.find((n) => n.slug === newsMatch[1]);
    if (article) {
      return {
        title: `${article.title} | ${SITE_NAME}`,
        description: truncate(article.summary),
        article,
      };
    }
  }
  return ROUTE_META[pathname] || DEFAULT_META;
};

// Updates the tags already present in public/index.html, so there is
// never a duplicate description/canonical in the head.
const setTag = (selector, attr, value) => {
  const el = document.head.querySelector(selector);
  if (el) el.setAttribute(attr, value);
};

const ARTICLE_SCRIPT_ID = 'article-jsonld';

const PageMeta = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const { title, description, article } = getMeta(pathname);
    const path = pathname.length > 1 ? pathname.replace(/\/$/, '') : '/';
    const url = `${SITE_URL}${path}`;

    document.title = title;
    setTag('meta[name="description"]', 'content', description);
    setTag('link[rel="canonical"]', 'href', url);
    setTag('meta[property="og:title"]', 'content', title);
    setTag('meta[property="og:description"]', 'content', description);
    setTag('meta[property="og:url"]', 'content', url);
    setTag('meta[property="og:type"]', 'content', article ? 'article' : 'website');
    setTag('meta[name="twitter:title"]', 'content', title);
    setTag('meta[name="twitter:description"]', 'content', description);

    document.getElementById(ARTICLE_SCRIPT_ID)?.remove();
    if (article) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = ARTICLE_SCRIPT_ID;
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline: article.title,
        description: article.summary,
        datePublished: article.publishDate,
        image: (article.images || [])
          .map((img) => (typeof img === 'string' ? img : img?.src))
          .filter(Boolean)
          .map((src) => new URL(src, SITE_URL).href),
        mainEntityOfPage: url,
        author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
        publisher: {
          '@type': 'Organization',
          name: SITE_NAME,
          logo: { '@type': 'ImageObject', url: `${SITE_URL}/genion_logo.png` },
        },
      });
      document.head.appendChild(script);
    }
  }, [pathname]);

  return null;
};

export default PageMeta;
